'use client';

import React, { useEffect, useMemo, useState } from 'react';

import { Color } from '@bytel/product-wall';
import { classNames } from '@bytel/product-wall/helpers';
import {
    Alignable,
    Column,
    Columns,
    Icon,
    IconName,
    Justifiable,
    Spacer,
    SpacerSize,
    View,
} from '@bytel/trilogy-react';

import { ProductChildFull, ProductParentFull, ProductType } from '@app-types/product';

import { EnergyLabel } from '@components/energy-label';
import { ImageThumbor } from '@components/image-thumbor';

import { Das } from '@product-detail/components';
import { sendProductPreviewTracking } from '@product-detail/helpers';

export type ProductPreviewProps = {
    features: ProductParentFull['features'];
    currentProduct: ProductChildFull;
    setCurrentProduct: (color: string, capacity?: string) => void;
    isRenewal: boolean;
    openEnergyLabelModal?: () => void;
};

export function ProductPreview({
    features,
    currentProduct,
    setCurrentProduct,
    isRenewal,
    openEnergyLabelModal,
}: Readonly<ProductPreviewProps>) {
    const [previewIndex, setPreviewIndex] = useState<number>(0);
    const [currentPreview, setCurrentPreview] = useState<string>(currentProduct.medias[previewIndex] ?? '');
    const [thumbnailIndex, setThumbnailIndex] = useState<number>(0);

    const isPhone = useMemo(() => currentProduct.type === ProductType.PHONE, [currentProduct.type]);

    const handleChangePreview = (index: number) => {
        setCurrentPreview(currentProduct.medias[index] as string);
        setPreviewIndex(index);
        sendProductPreviewTracking(isRenewal);
    };

    useEffect(() => {
        setThumbnailIndex(0);
        setCurrentPreview(currentProduct.medias[previewIndex] ?? (currentProduct.medias[0] as string));
    }, [currentProduct]);

    return (
        <div className='is-sticky-top-desktop product-preview'>
            <Columns align={Alignable.ALIGNED_CENTER} mobile marginless>
                <div className='is-relative'>
                    <div className='product-thumbnail'>
                        <Column narrow verticalAlign={Alignable.ALIGNED_CENTER}>
                            <View flexable className='gap-medium is-flex-col' align={Alignable.ALIGNED_CENTER}>
                                <Icon
                                    name={IconName.ARROW_UP}
                                    onClick={() => setThumbnailIndex(thumbnailIndex - 1)}
                                    className={classNames('cursor-pointer', thumbnailIndex > 0 ? '' : 'is-invisible')}
                                />
                                {currentProduct?.medias.map(
                                    (media, index) =>
                                        index >= thumbnailIndex &&
                                        index <= thumbnailIndex + 2 && (
                                            <div
                                                key={media}
                                                className='cursor-pointer scale-1-hover-effect'
                                                onClick={() => handleChangePreview(index)}
                                            >
                                                <ImageThumbor
                                                    src={`/${media}`}
                                                    alt=''
                                                    width={40}
                                                    height={isPhone ? 80 : 40}
                                                    position={0}
                                                    fetchPriority='high'
                                                />
                                            </div>
                                        ),
                                )}
                                <Icon
                                    name={IconName.ARROW_DOWN}
                                    onClick={() => setThumbnailIndex(thumbnailIndex + 1)}
                                    className={classNames(
                                        'cursor-pointer',
                                        thumbnailIndex < currentProduct?.medias.length - 3 ? '' : 'is-invisible',
                                    )}
                                />
                            </View>
                        </Column>
                    </div>
                    <Column>
                        <ImageThumbor
                            src={`/${currentPreview}`}
                            alt={`Photo du produit ${currentProduct.parentTitle}`}
                            aria-hidden='true'
                            width={200}
                            height={isPhone ? 400 : 200}
                            id='productPreview'
                            position={0}
                            fetchPriority='high'
                        />
                    </Column>
                </div>
            </Columns>

            <Spacer size={SpacerSize.FOUR} />
            <View flexable justify={Justifiable.JUSTIFIED_CENTER} className='gap-medium'>
                {features.color.map(({ name, value }) => (
                    <Color
                        key={name}
                        color={value}
                        isFocus={currentProduct.color === name}
                        onClick={() => setCurrentProduct(name, currentProduct.capacity)}
                    />
                ))}
            </View>
            <Spacer size={SpacerSize.FOUR} />
            <View flexable justify={Justifiable.JUSTIFIED_CENTER} className='gap-medium'>
                {currentProduct.characteristics.energyLabel && (
                    <EnergyLabel
                        rating={currentProduct.characteristics.energyLabel.label}
                        onClick={openEnergyLabelModal}
                    />
                )}
                <Das das={currentProduct.das} />
            </View>
        </div>
    );
}
